import CubiscanIcon from '../../pages/settings/components/add-on-tile/images/cubiscan_icon.svg';
import FulfillmentProviderIcon from '../../pages/settings/components/add-on-tile/images/3pl_icon.svg';
import DropShipIcon from '../../pages/settings/components/add-on-tile/images/dropship_icon.svg';
import OdbcIcon from '../../pages/settings/components/add-on-tile/images/odbc_icon.svg';
import ProductBundleIcon from '../../pages/settings/components/add-on-tile/images/bundle-icon.svg';
import ShippingStrategiesIcon from '../../pages/settings/components/add-on-tile/images/rate_shopper_icon.svg';
import AutoSplitIcon from '../../pages/settings/components/add-on-tile/images/auto_split_icon.svg';
import InventorySyncIcon from '../../pages/settings/components/add-on-tile/images/inventory_sync_icon.svg';
import InventoryApiIcon from '../../pages/settings/components/add-on-tile/images/inventory-api-icon.svg';
import ShipStationReturnsIcon from '../../pages/settings/components/add-on-tile/images/shipstation-returns-icon.svg';
import ApiIcon from '../../pages/settings/components/add-on-tile/images/api_icon.svg';

import cubiscan from '../../components/upsell-modal/modal-svgs/cubiscan.svg';
import odbc from '../../components/upsell-modal/modal-svgs/odbc.svg';
import fulfillmentProviders from '../../components/upsell-modal/modal-svgs/3pl.svg';
import dropship from '../../components/upsell-modal/modal-svgs/dropship.svg';
import productBundles from '../../components/upsell-modal/modal-svgs/product-bundles.svg';
import shippingStrategies from '../../components/upsell-modal/modal-svgs/rate-shopper.svg';
import autoSplit from '../../components/upsell-modal/modal-svgs/auto-split.svg';
import inventorySync from '../../components/upsell-modal/modal-svgs/inventory_sync.svg';
import returnRabbit from '../../components/upsell-modal/modal-svgs/return-rabbit.jpg';
import api from './modal-svgs/api.svg';
import inventoryApi from './modal-svgs/inventory-api.svg';
import enhancedReturns from './modal-svgs/enhanced-returns.svg';
import genericUpsellImage from './modal-svgs/generic-upsell-image.svg';

import type { MessageDescriptor } from 'react-intl';

import { linkIDs } from '../../services/link-service/links';
import { messages, upsellModalContent } from './messages';
import { UpsellModalContentCodes } from '../../constants/upsell-modal-content-codes';

import type { UpsellModalContent } from './types';
import type { UpsellModalContentCode } from '../../constants/upsell-modal-content-codes';
import { BillingPlanIds } from '../../enums/billing-plan-ids';

const alignImageBottomFeatureCodes = [UpsellModalContentCodes.ODBC];
const shouldUseAlternateBackground = [UpsellModalContentCodes.ReturnRabbit];

const upsellContentLookupMap = {
  [UpsellModalContentCodes.Cubiscan]: {
    icon: CubiscanIcon,
    upsellImageUri: cubiscan,
    kbArticle: linkIDs.CubiscanDimensioners,
  },
  [UpsellModalContentCodes.DropShip]: {
    icon: DropShipIcon,
    upsellImageUri: dropship,
    kbArticle: linkIDs.DropshipManager,
  },
  [UpsellModalContentCodes.ODBC]: {
    icon: OdbcIcon,
    upsellImageUri: odbc,
    kbArticle: linkIDs.ODBCOverview,
  },
  [UpsellModalContentCodes.FulfillmentProviderBundle]: {
    icon: FulfillmentProviderIcon,
    upsellImageUri: fulfillmentProviders,
    kbArticle: linkIDs.ThirdPartyLogisticsProvider,
  },
  [UpsellModalContentCodes.ProductBundles]: {
    icon: ProductBundleIcon,
    upsellImageUri: productBundles,
    kbArticle: linkIDs.ProductBundlesOverview,
  },
  [UpsellModalContentCodes.ShippingStrategies]: {
    icon: ShippingStrategiesIcon,
    upsellImageUri: shippingStrategies,
    kbArticle: linkIDs.RateShopperHelp,
  },
  [UpsellModalContentCodes.AutoSplit]: {
    icon: AutoSplitIcon,
    upsellImageUri: autoSplit,
    kbArticle: linkIDs.AutoSplitOverview,
  },
  [UpsellModalContentCodes.InventorySync]: {
    icon: InventorySyncIcon,
    upsellImageUri: inventorySync,
    kbArticle: linkIDs.InventorySync,
  },
  [UpsellModalContentCodes.ReturnRabbit]: {
    icon: ShipStationReturnsIcon,
    upsellImageUri: returnRabbit,
    kbArticle: linkIDs.ReturnRabbit,
  },
  [UpsellModalContentCodes.InventoryApi]: {
    icon: InventoryApiIcon,
    upsellImageUri: inventoryApi,
    kbArticle: linkIDs.ShipstationApiDocs,
  },
  [UpsellModalContentCodes.ShipStationApiV2]: {
    icon: ApiIcon,
    upsellImageUri: api,
    kbArticle: linkIDs.ShipstationV2ApiDocs,
  },
  [UpsellModalContentCodes.BarcodeScanning]: {
    upsellImageUri: genericUpsellImage,
  },
  [UpsellModalContentCodes.DashboardsViewOnly]: {
    upsellImageUri: genericUpsellImage,
  },
  [UpsellModalContentCodes.EnhancedReturns]: {
    upsellImageUri: enhancedReturns,
  },
  [UpsellModalContentCodes.ProductCatalog]: {
    upsellImageUri: inventorySync,
  },
};

export const getUpsellModalContent = (upsellModalContentCode: UpsellModalContentCode): UpsellModalContent => {
  return {
    ...upsellModalContent[upsellModalContentCode],
    ...upsellContentLookupMap[upsellModalContentCode],
  } as UpsellModalContent;
};

export const getUpsellItemName = (upsellContentCode: UpsellModalContentCode): MessageDescriptor | undefined => {
  return upsellModalContent[upsellContentCode]?.name;
};

export const shouldAlignContentImageDown = (upsellContentCode: UpsellModalContentCode): boolean => {
  return alignImageBottomFeatureCodes.includes(upsellContentCode);
};

export const shouldUseAlternateBackgroundImage = (upsellContentCode: UpsellModalContentCode): boolean => {
  return shouldUseAlternateBackground.includes(upsellContentCode);
};

const billingPlanBulletsMap: Record<number, MessageDescriptor> = {
  [BillingPlanIds.Starter2025Q2]: messages.starterPlanBullets,
  [BillingPlanIds.Standard2025Q2]: messages.standardPlanBullets,
  [BillingPlanIds.Premium2025Q2]: messages.premiumPlanBullets,
};

export const getBulletsForBillingPlan = (billingPlanId: number): MessageDescriptor | undefined => {
  return billingPlanBulletsMap[billingPlanId];
};
